// stockImageLibrary.logic.js — pure, I/O-free logic for the curated Stock Image
// library source (LIB-image-library). Unit-tested in StockImageLibrary.test.jsx.
//
// The curated set is the SAME one the Wallpaper "Preset images" grid already ships
// (proceduralLibrary/presets/manifest -> PRESETS). We reuse it verbatim as the
// single source of truth so the wallpaper grid and the shared media picker can
// never drift (everything-is-a-shared-component). This module only owns the
// picker-agnostic mapping: a preset -> the value shape MediaSelection.handleChange
// already receives from Upload/Browse/Recent ({ src, url, title, thumbnail, type }).

import { PRESETS } from "../proceduralLibrary/presets/manifest";

// The stock items surfaced by the picker. `full` (absolutised webp) is the value
// applied + persisted; `thumb` is only ever an <img> src.
export const STOCK_IMAGE_ITEMS = PRESETS.map((p) => ({
  id: p.id,
  label: p.label,
  thumbnail: p.thumb,
  src: p.full,
  type: "image",
}));

// Build the value handed to the consumer's onSelect/handleChange when a stock
// image is chosen — mirrors the Upload/Browse/Recent/Library shape so every
// downstream consumer (portrait, image block, business box, wallpaper) keeps
// working unchanged. `src`/`url` is the canonical absolute URL every consumer reads.
export function toStockInsertValue(item) {
  if (!item) return null;
  const url = item.src || item.url || "";
  return {
    id: item.id ?? url,
    src: url,
    url,
    title: item.label || item.title || "",
    thumbnail: item.thumbnail || url,
    type: "image",
  };
}

// Should the Stock source be offered for this builder media `type`? Stock images
// are images only — never offered for video/audio/document surfaces.
export function isStockEligibleType(builderType) {
  if (!builderType) return false;
  return ["image", "images"].includes(String(builderType).toLowerCase());
}

// Normalise an image URL to a stable comparison key so the "currently selected"
// check-mark survives harmless URL divergence (NEM F7). The persisted `selectedSrc`
// (what the consumer stored) and the tile's `item.src` (absolutised full webp) can
// legitimately differ by a query string, a protocol-relative vs. absolute origin, or
// a stored thumbnail vs. full — a raw `===` then silently drops the check-mark on
// reopen. We compare on the trailing path basename (filename), lower-cased, with the
// query/hash stripped — the one part that is stable across those variants for our
// same-file preset assets.
export function normaliseStockKey(url) {
  if (!url || typeof url !== "string") return "";
  // Drop query + hash, then take the last path segment (basename).
  const noQuery = url.split(/[?#]/)[0];
  const segments = noQuery.split("/");
  const basename = segments[segments.length - 1] || noQuery;
  return basename.toLowerCase();
}

// Does this tile represent the consumer's currently-selected image? Compares on the
// normalised key, not raw string equality, so the marker is robust to URL variants.
export function isStockItemSelected(item, selectedSrc) {
  if (!item || selectedSrc == null || selectedSrc === "") return false;
  const key = normaliseStockKey(item.src);
  if (!key) return false;
  return (
    normaliseStockKey(selectedSrc) === key ||
    normaliseStockKey(item.thumbnail) === normaliseStockKey(selectedSrc)
  );
}
